import React, { useState, useEffect } from 'react';
import './DeviceManager.css';
import '../styles/clean-dashboard.css';
import QRCodeModal from './QRCodeModal';
import DeviceForm from './DeviceForm';
import { getApiBaseUrl } from '../config/api';

const DeviceManager = ({ instances, onInstancesChange, onSelectInstance, onOpenChat, user }) => {
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedDeviceForQR, setSelectedDeviceForQR] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);

  // Add useEffect to log instances changes
  useEffect(() => {
    console.log('DeviceManager received instances:', instances);
    console.log('Number of instances:', instances.length);
  }, [instances]);

  const handleAddDevice = () => {
    setEditingDevice(null);
    setShowAddDevice(true);
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setShowAddDevice(true);
  };

  const handleShowQR = (device) => {
    setSelectedDeviceForQR(device);
    setShowQRModal(true);
  };

  const handleConnectDevice = async (deviceId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${deviceId}/connect`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        onInstancesChange();
        // Show QR code after connecting
        const device = instances.find(d => d.id === deviceId);
        if (device) {
          handleShowQR(device);
        }
      } else {
        alert('Failed to connect device: ' + data.error);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    }
  };

  const handleDisconnectDevice = async (deviceId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to disconnect this device?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${deviceId}/disconnect`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        onInstancesChange();
      } else {
        alert('Failed to disconnect device: ' + data.error);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this device? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${deviceId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        onInstancesChange();
      } else {
        alert('Failed to delete device: ' + data.error);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return '#28a745';
      case 'connecting': return '#ffc107';
      case 'disconnected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected': return 'Connected & Authenticated';
      case 'connecting': return 'Connected but needs QR scan';
      case 'disconnected': return 'Disconnected';
      default: return 'Unknown';
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'connected': return 'Ready to send/receive messages';
      case 'connecting': return 'Please scan QR code to authenticate';
      case 'disconnected': return 'Click Connect to start';
      default: return '';
    }
  };

  return (
    <div className="device-manager">
      <div className="device-header">
        <h2>WhatsApp Devices</h2>
        {(user.role === 'super_admin') && (
          <button className="add-device-btn" onClick={handleAddDevice}>
            <span>+</span> Add Device
          </button>
        )}
      </div>

      <div className="devices-grid">
        {instances.length === 0 ? (
          <div className="no-devices">
            <div className="no-devices-icon">📱</div>
            <h3>No devices found</h3>
            {(user.role === 'super_admin') ? (
              <>
                <p>Add your first WhatsApp device to get started</p>
                <button className="add-first-device-btn" onClick={handleAddDevice}>
                  Add First Device
                </button>
              </>
            ) : (
              <p>No devices assigned to you. Please contact your administrator for device access.</p>
            )}
          </div>
        ) : (
          instances.map(device => (
            <div key={device.id} className="device-card">
              <div className="device-card-header">
                <div className="device-info">
                  <h3>{device.name}</h3>
                  <p className="device-phone">{device.phone_number || 'No phone number'}</p>
                </div>
                <div className="device-status-container">
                  <div 
                    className="device-status"
                    style={{ backgroundColor: getStatusColor(device.status) }}
                  >
                    {getStatusText(device.status)}
                  </div>
                  <small className="status-description">
                    {getStatusDescription(device.status)}
                  </small>
                </div>
              </div>

              <div className="device-details">
                <div className="detail-item">
                  <span className="label">API Key:</span>
                  <span className="value">{device.api_key ? '••••••••' : 'Not set'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Webhook:</span>
                  <span className="value">{device.webhook_url || 'Not set'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Created:</span>
                  <span className="value">
                    {new Date(device.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="device-actions">
                {device.status === 'connected' ? (
                  <>
                    <button 
                      className="action-btn primary"
                      onClick={() => onOpenChat ? onOpenChat(device) : onSelectInstance(device)}
                    >
                      Open Chat
                    </button>
                    {( user.role === 'super_admin') && (
                      <>
                        <button 
                          className="action-icon-btn secondary"
                          onClick={() => handleShowQR(device)}
                          title="Show QR Code"
                        >
                          📱
                        </button>
                        <button 
                          className="action-icon-btn warning"
                          onClick={() => handleDisconnectDevice(device.id)}
                          title="Disconnect Device"
                        >
                          🔌
                        </button>
                      </>
                    )}
                  </>
                ) : device.status === 'connecting' ? (
                  <>
                    {( user.role === 'super_admin') ? (
                      <>
                        <button 
                          className="action-icon-btn primary"
                          onClick={() => handleShowQR(device)}
                          title="Scan QR Code"
                        >
                          📱
                        </button>
                        <button 
                          className="action-icon-btn warning"
                          onClick={() => handleDisconnectDevice(device.id)}
                          title="Disconnect Device"
                        >
                          🔌
                        </button>
                      </>
                    ) : (
                      <div className="user-message">
                        <p className="status-message">Device is connecting. Please contact admin to complete setup.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {( user.role === 'super_admin') ? (
                      <button 
                        className="action-btn success"
                        onClick={() => handleConnectDevice(device.id)}
                      >
                        Connect
                      </button>
                    ) : (
                      <div className="user-message">
                        <p className="status-message">Device is disconnected. Please contact admin to connect.</p>
                      </div>
                    )}
                  </>
                )}

                {(user.role === 'super_admin') && (
                  <div className="device-actions-menu">
                    <button 
                      className="action-icon-btn edit-btn"
                      onClick={() => handleEditDevice(device)}
                      title="Edit Device"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-icon-btn delete-btn"
                      onClick={() => handleDeleteDevice(device.id)}
                      title="Delete Device"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showAddDevice && (
        <DeviceForm
          device={editingDevice}
          onClose={() => setShowAddDevice(false)}
          onSave={() => {
            setShowAddDevice(false);
            onInstancesChange();
          }}
        />
      )}

      {showQRModal && selectedDeviceForQR && (
        <QRCodeModal
          device={selectedDeviceForQR}
          onClose={() => setShowQRModal(false)}
          onConnected={onInstancesChange}
        />
      )}
    </div>
  );
};

export default DeviceManager;
